/*************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 **************************************************************************/

import { SEARCH_NEW_FILTERS_LOCATION_OWNERSHIP_TYPE_MAP } from '../../containers/SearchResultsPage/SearchConstants';
import SearchConfig from '../../utils/SearchConfig';
import SearchUtils from '../../utils/SearchUtils';
import SearchHostContext from '../../utils/SearchHostContext';

type FilterItems = {
	scope?: USSScopes[];
	assets?: Array<{
		type?: {
			inclusions?: DocumentTypes[];
			exclusions?: DocumentTypes[];
		};
		container?: {
			inclusions?: ContainerType[];
			exclusions?: ContainerType[];
		};
		ownershipType?: {
			inclusions?: OwnershipType[];
			exclusions?: OwnershipType[];
		};
		parentId?: {
			inclusions?: string[];
			exclusions?: string[];
		};
	}>;
	dateRange?: DateRange;
};
class FilterProcessor {
	static instance: FilterProcessor | null;
	private searchHostContext: HostContext;

	constructor() {
		if (FilterProcessor.instance) {
			return FilterProcessor.instance;
		}
		FilterProcessor.instance = this;
		this.searchHostContext = SearchHostContext.getContext();
	}

	public processSelectedModifiedDateFilter(filter: SelectedUiFilters): DateRange | null {
		if (filter.dateModified) {
			const dateRange = this.getSortDateRangeFromOption(filter.dateModified);
			return dateRange;
		}
		return null;
	}

	/**
	 * Gets the date range object for filters given a date option.
	 * @param {string} dateOption
	 * @returns {DateRange}
	 */
	private getSortDateRangeFromOption(dateOption: DateFilterString): DateRange {
		const dateRange: DateRange = {
			min: 0,
			max: 0,
		};

		switch (dateOption) {
			case 'today': {
				const beginningOfTodayDate = new Date();
				// get the beginning of today
				beginningOfTodayDate.setHours(0);
				beginningOfTodayDate.setMinutes(0);
				dateRange.min = beginningOfTodayDate.getTime();
				dateRange.max = new Date().getTime();
				break;
			}

			case 'yesterday': {
				const yesterdayDate = this.getDateRangeEndDateFromOption(dateOption);
				const beginningOfYesterdayDate = new Date(yesterdayDate);
				const endOfYesterdayDate = new Date(yesterdayDate);

				// get the beginning of yesterday
				beginningOfYesterdayDate.setHours(0);
				beginningOfYesterdayDate.setMinutes(0);
				// get the end of yesterday
				endOfYesterdayDate.setHours(23);
				endOfYesterdayDate.setMinutes(59);

				dateRange.min = beginningOfYesterdayDate.getTime();
				dateRange.max = endOfYesterdayDate.getTime();
				break;
			}

			case 'lastSevenDays':
			case 'lastThirtyDays':
			case 'lastNinetyDays':
			case 'lastYear': {
				const nowDate = new Date();
				const startDate = this.getDateRangeEndDateFromOption(dateOption);
				dateRange.min = startDate.getTime();
				dateRange.max = nowDate.getTime();
				break;
			}
		}

		return dateRange;
	}

	/**
	 * Takes the string provided from the filterOptions for a date
	 * Converts it into an actual Date object to be used for comparison.
	 * @param {string} dateOption
	 */
	private getDateRangeEndDateFromOption(dateOption: DateFilterString): Date {
		// Get the Date object from the corresponding string time.
		const todayDate = new Date();
		const dateToReturn = new Date();

		switch (dateOption) {
			case 'yesterday':
				dateToReturn.setDate(todayDate.getDate() - 1);
				break;

			case 'lastSevenDays':
				dateToReturn.setDate(todayDate.getDate() - 7);
				break;

			case 'lastThirtyDays':
				dateToReturn.setDate(todayDate.getDate() - 30);
				break;

			case 'lastNinetyDays':
				dateToReturn.setDate(todayDate.getDate() - 90);
				break;

			case 'lastYear':
				dateToReturn.setFullYear(todayDate.getFullYear() - 1);
				break;
			// we don't specify anything here as new Date() itself has 'today' in it.
			case 'today':
		}

		dateToReturn.setHours(0);
		dateToReturn.setMinutes(0);
		return dateToReturn;
	}

	private processSelectedTypeFilters(filter: SelectedUiFilters): DocumentTypes[] {
		let documentTypesSelected: DocumentTypes[] = [];
		if (filter.type) {
			documentTypesSelected = [...filter.type].map((t) => {
				if (String(t) === 'Project') {
					return 'Projects';
				}
				return t;
			}) as DocumentTypes[];
			// de-dupe after normalization
			documentTypesSelected = [...new Set(documentTypesSelected)];
		}
		return documentTypesSelected;
	}

	/**
	 * Given a source type, return the scope type.
	 * @param {SourceTypes} source - The source of the image.
	 * @returns The scope of the source.
	 */
	private getScopeBasedOnSource(source: SourceTypes) {
		return source === 'Lightroom' ? 'Lightroom' : 'CreativeCloud';
	}

	/**
	 * Add type exclusions in case of "YourLibraries" and "TeamLibraries"
	 *
	 * @param {SourceTypes} source - SourceTypes
	 * @returns The filterMapOption object.
	 */
	private updateMetaDataFromSearchConfig(source: SourceTypes) {
		const filterMapOption = SEARCH_NEW_FILTERS_LOCATION_OWNERSHIP_TYPE_MAP[source as string];

		if (source === 'YourLibraries' || source === 'TeamLibraries') {
			// TODO check : why do we need exclusions , inclusions should be sufficient
			const searchConfig = SearchConfig.getSearchConfig();
			const searchConfigFilterOptions = searchConfig.ui && searchConfig.ui.filterOptions;
			const typeFilterOptions = (searchConfigFilterOptions[1].optionsToDisplay as DocumentTypes[]).map((type) => {
				if (String(type) === 'Project') {
					return 'Projects' as DocumentTypes;
				}
				return type;
			}) as DocumentTypes[];

			const optionsToBeExcluded = typeFilterOptions.filter(
				(type: DocumentTypes) => type !== 'Library' && type !== 'LibraryElement',
			);

			filterMapOption.type.exclusions = filterMapOption.type.exclusions || [];
			filterMapOption.type.exclusions.push(...optionsToBeExcluded);
		}

		return filterMapOption;
	}

	private processSelectedSourceFilters(
		filter: SelectedUiFilters,
		documentTypesSelected: DocumentTypes[],
	): [Set<USSScopes>, unknown[]] {
		const scopesSelected: Set<USSScopes> = new Set();
		const partialFilterItems = [];

		if (filter.source && filter.source.length > 0) {
			filter.source.forEach((option) => {
				scopesSelected.add(this.getScopeBasedOnSource(option));

				const filterMapOption = this.updateMetaDataFromSearchConfig(option);
				let partialFilterItem = {
					type: {
						inclusions: [...documentTypesSelected],
						exclusions: [],
					},
					ownershipType: {
						inclusions: [],
						exclusions: [],
					},
					container: {
						inclusions: [],
						exclusions: [],
					},
				};
				if (filterMapOption.ownershipType) {
					filterMapOption.ownershipType.inclusions &&
						partialFilterItem.ownershipType.inclusions.push(...filterMapOption.ownershipType.inclusions);

					filterMapOption.ownershipType.exclusions &&
						partialFilterItem.ownershipType.exclusions.push(...filterMapOption.ownershipType.exclusions);
				}

				if (filterMapOption.container) {
					filterMapOption.container.inclusions &&
						partialFilterItem.container.inclusions.push(...filterMapOption.container.inclusions);

					filterMapOption.container.exclusions &&
						partialFilterItem.container.exclusions.push(...filterMapOption.container.exclusions);
				}

				if (filterMapOption.type) {
					documentTypesSelected.length <= 0 &&
						filterMapOption.type.inclusions &&
						partialFilterItem.type.inclusions.push(...filterMapOption.type.inclusions);

					filterMapOption.type.exclusions &&
						partialFilterItem.type.exclusions.push(...filterMapOption.type.exclusions);
				}
				partialFilterItem = {
					type: {
						inclusions: [...new Set(partialFilterItem.type.inclusions)],
						exclusions: [...new Set(partialFilterItem.type.exclusions)],
					},
					ownershipType: {
						inclusions: [...new Set(partialFilterItem.ownershipType.inclusions)],
						exclusions: [...new Set(partialFilterItem.ownershipType.exclusions)],
					},
					container: {
						inclusions: [...new Set(partialFilterItem.container.inclusions)],
						exclusions: [...new Set(partialFilterItem.container.exclusions)],
					},
				};

				// delete empty keys
				partialFilterItem.type.inclusions.length <= 0 &&
					partialFilterItem.type.exclusions.length <= 0 &&
					delete partialFilterItem.type;

				partialFilterItem.ownershipType.inclusions.length <= 0 &&
					partialFilterItem.ownershipType.exclusions.length <= 0 &&
					delete partialFilterItem.ownershipType;

				partialFilterItem.container.inclusions.length <= 0 &&
					partialFilterItem.container.exclusions.length <= 0 &&
					delete partialFilterItem.container;

				Object.keys(partialFilterItem).length > 0 && partialFilterItems.push(partialFilterItem);
			});
		}

		return [scopesSelected, partialFilterItems];
	}

	/**
	 * !HACK - This is a temporary fix to help server identify between space and folder since both have same mimeType
	 * Add inclusion/ exclusion based on whether the folder or space is selected
	 * @param filterItems
	 */
	private processForFolderAndSpace(filterItems: FilterItems): void {
		if (filterItems.assets) {
			filterItems.assets.forEach((asset) => {
				if (asset.type) {
					const folderTypeSelected = asset.type.inclusions.includes('Folder');
					const spaceTypeSelected = asset.type.inclusions.includes('Space');
					if (folderTypeSelected || spaceTypeSelected) {
						if (!asset.container) {
							asset.container = {
								inclusions: [],
								exclusions: [],
							};
						}
						if (!folderTypeSelected && spaceTypeSelected) {
							asset.container.inclusions.push('ToplevelSpaces');
						} else if (folderTypeSelected && !spaceTypeSelected) {
							asset.container.exclusions.push('ToplevelSpaces');
							asset.container.exclusions.push('Projects');
						}
					}
				}
			});
		}
	}

	/**
	 * Add inclusion/ exclusion based on whether the folder or space is selected
	 * @param filterItems
	 */
	private processHostFlagOverrides(filterItems: FilterItems, hostContext?: HostContext): void {
		const spacesUIEnabledForHost = SearchUtils.isAssetCategoryUIEnabled('space', hostContext, 'CCDesktop');
		if (!spacesUIEnabledForHost) {
			if (filterItems.assets && filterItems.scope.includes('CreativeCloud')) {
				filterItems.assets.forEach((asset) => {
					if (asset.container) {
						asset.container.exclusions && asset.container.exclusions.length
							? asset.container.exclusions.push('Spaces')
							: (asset.container.exclusions = ['Spaces']);
					} else {
						asset.container = {
							exclusions: ['Spaces'],
						};
					}
				});
			}
		}
	}

	/**
	 * Add project in container when type is selected as project
	 * @param filterItems
	 */
	private processProjectInTypeFilter(filterItems: FilterItems): void {
		if (!filterItems.assets) {
			return;
		}
		filterItems.assets.forEach((asset) => {
			if (!asset.type || !asset.type.inclusions) {
				return;
			}
			const hasProjectInType = asset.type.inclusions.includes('Projects');
			if (!hasProjectInType) {
				return;
			}
			// ensure container exists with inclusions and exclusions arrays
			if (!asset.container) {
				asset.container = {
					inclusions: [],
					exclusions: [],
				};
			}
			const inclusionsSet = new Set(asset.container.inclusions || []);
			inclusionsSet.add('Projects');
			inclusionsSet.delete('ProjectsAssets');
			asset.container.inclusions = Array.from(inclusionsSet);

			const exclusionsSet = new Set(asset.container.exclusions || []);
			exclusionsSet.add('ProjectsAssets');
			asset.container.exclusions = Array.from(exclusionsSet);
		});
	}

	private processBrandAndBrandElementFilters(filterItems: FilterItems): void {
		if (!filterItems.assets) {
			return;
		}
		filterItems.assets.forEach((asset) => {
			if (!asset.type || !asset.type.inclusions) {
				return;
			}

			const hasBrandInType = asset.type.inclusions.includes('Brand');
			const hasBrandElementInType = asset.type.inclusions.includes('BrandElement');
			if (!hasBrandInType && !hasBrandElementInType) {
				return;
			}
			// ensure container exists with inclusions and exclusions arrays
			if (!asset.container) {
				asset.container = {
					inclusions: [],
					exclusions: [],
				};
			}
			const inclusionsSet = new Set(asset.container.inclusions);

			if (hasBrandInType) {
				inclusionsSet.add('Brand');
			}

			if (hasBrandElementInType) {
				inclusionsSet.add('BrandElement');
			}

			asset.container.inclusions = Array.from(inclusionsSet);
		});
	}
	private processFilters(filters: SelectedUiFilters, hostContext?: HostContext): FilterItems {
		const filterItems: FilterItems = {
			assets: [],
			scope: ['CreativeCloud', 'Lightroom'],
		};

		// all the selected document types
		// scopes which are selected in source
		let scopesSelected: Set<USSScopes> = new Set();
		let partialFilterItems = [];

		const dateRange: DateRange = this.processSelectedModifiedDateFilter(filters);
		const documentTypesSelected: DocumentTypes[] = this.processSelectedTypeFilters(filters);
		[scopesSelected, partialFilterItems] = this.processSelectedSourceFilters(filters, documentTypesSelected);

		// add dateRange if any date filter selected
		dateRange && (filterItems.dateRange = dateRange);
		// add scopes if any source filter was selected
		scopesSelected.size > 0 && (filterItems.scope = [...scopesSelected]);

		if (partialFilterItems.length === 0) {
			filterItems.assets.push({
				type: {
					inclusions: documentTypesSelected,
				},
			});
		} else {
			filterItems.assets.push(...partialFilterItems);
		}

		this.processProjectInTypeFilter(filterItems);

		this.processBrandAndBrandElementFilters(filterItems);

		// process for folder and space
		this.processForFolderAndSpace(filterItems);
		this.processHostFlagOverrides(filterItems, hostContext);

		return filterItems;
	}

	getAssetRequestFilters(filter: SelectedUiFilters, hostContext?: HostContext) {
		const filterToUSSRequestObject = this.processFilters(filter, hostContext);
		const filters = [];
		filterToUSSRequestObject.scope &&
			filterToUSSRequestObject.scope.forEach((scope: string) => {
				filters.push(
					Object.assign(
						{},
						{
							...filterToUSSRequestObject,
							scope: [scope],
							...(filterToUSSRequestObject.dateRange && {
								dateRange: Object.assign(
									{},
									{
										...filterToUSSRequestObject.dateRange,
										event: scope === 'Lightroom' ? 'RepoCreated' : 'Modified',
									},
								),
							}),
						},
					),
				);
			});

		return filters;
	}
}

export default new FilterProcessor();
